import { CardExpiryStatus } from '@afterpay/types'
import { CardErrorReason, PaymentMethodType } from '../../model'
import { buildTranslationKey } from '../../utils/locales'
import { SwitchContext } from './types'

export const isKeyWithInlineContext = (reason: string, context?: SwitchContext): boolean => {
  const keysWithInlineContext: Record<string, SwitchContext[]> = {
    cardExpiry: [SwitchContext.CannotSwitch],
    foreignCard: [],
    prepaidCard: [],
    invalidBrand: [SwitchContext.CannotSwitch],
    unacceptableCard: [SwitchContext.CannotSwitch, SwitchContext.Switched],
    unacceptableIssuerRestrictedCard: [SwitchContext.CannotSwitch, SwitchContext.Switched],
    fallbackGenericMessage: [SwitchContext.Switched],
    checkingNotAllowed: [SwitchContext.Switched],
    creditCardNotAllowed: [SwitchContext.CannotSwitch],
    cashAppCardIneligible: [SwitchContext.CannotSwitch, SwitchContext.Switched],
  }

  if (!Object.keys(keysWithInlineContext).includes(reason)) {
    return false
  }

  const validContexts = keysWithInlineContext[reason]

  const isValidNoContext = !context && validContexts.length === 0
  const isValidWithContext = !!context && validContexts.includes(context)

  return isValidNoContext || isValidWithContext
}

export const getPreferredPaymentMethodKey = (paymentMethodType: PaymentMethodType | undefined): string => {
  switch (paymentMethodType) {
    case PaymentMethodType.ACH_BANK_ACCOUNT:
      return 'bank'
    case PaymentMethodType.APPLE_PAY:
      return 'applePay'
    default:
      return 'card'
  }
}

const HAS_EXPIRED_OVERRIDE_REASONS: ReadonlyArray<CardErrorReason> = ['cardExpiry', 'cardExpiryLimitInMonths']

interface CardErrorMessageInput {
  reason: CardErrorReason
  isArcadeDesignLanguage: boolean
  switched: boolean | undefined
  cardExpiryStatus: CardExpiryStatus | undefined
  isAFFPBI: boolean
  preferredPaymentMethod: PaymentMethodType | undefined
}

interface CardErrorMessageSelection {
  messageKey: string
  context: string
}

export const selectCardErrorMessage = ({
  reason,
  isArcadeDesignLanguage,
  switched,
  cardExpiryStatus,
  isAFFPBI,
  preferredPaymentMethod,
}: CardErrorMessageInput): CardErrorMessageSelection => {
  const baseContext = switched ? SwitchContext.Switched : SwitchContext.CannotSwitch
  const inlineContext = isArcadeDesignLanguage && isKeyWithInlineContext(reason, baseContext) ? `${baseContext}Inline` : baseContext

  const useHasExpiredCopy =
    isArcadeDesignLanguage && !switched && cardExpiryStatus === CardExpiryStatus.EXPIRED && HAS_EXPIRED_OVERRIDE_REASONS.includes(reason)

  if (useHasExpiredCopy) {
    return { messageKey: 'summary:error.cardExpiry.message', context: 'hasExpiredInline' }
  }

  return {
    messageKey: buildTranslationKey({
      namespace: 'summary',
      segments: [
        'error',
        reason,
        {
          contexts: [[isAFFPBI && reason === 'creditCardNotAllowed', `message_aff_${getPreferredPaymentMethodKey(preferredPaymentMethod)}`]],
          default: 'message',
        },
      ],
    }),
    context: inlineContext,
  }
}
